import React, { FC, useEffect, useMemo, useState } from "react";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useAppTheme } from "@/utils/theme";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import AlertDialog from "@/components/AlertDialog";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import SearchBox from "@/components/SearchBox";
import useDebounce from "@/hooks/useDebounce";
import { useSettings, useSettingDeleteOne } from "@/hooks/useSetting";
import { Setting } from "@/domain/setting.interface";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import { SettingModal } from "@/ui/modals/settings/SettingModal";
import ActionMenu from "@/components/ActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";

interface Filters {
  sortField: string;
  sortOrder: number;
  search: string;
  pageNumber: number;
  pageSize: number;
}

const SettingsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();

  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();

  const { alertState, closeAlert, confirmAction } = useAlertModal();

  const createAuditLog = useCreateAuditlog();
  const deleteSettingOne = useSettingDeleteOne();

  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle("Settings");
  }, []);

  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedSetting, setSelectedSetting] = useState<Setting | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    setting: Setting | null;
  }>({
    open: false,
    setting: null,
  });

  const [filters, setFilters] = useState<Filters>({
    sortField: "name",
    sortOrder: 1,
    search: "",
    pageNumber: 0,
    pageSize: 10,
  });

  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  const { settings, totalCount, isLoadingSettings, refetchSettings } =
    useSettings({
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      search: filters.search,
    });

  const rows = useMemo(() => {
    if (!settings || settings.length === 0) return [];

    return settings.map((setting, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            setSelectedSetting(setting);
            setModalMode("edit");
            setIsSettingModalOpen(true);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setShowDeleteModal({ open: true, setting });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const row = createDataRow(setting._id || index, {
        name: <Typography variant="bodySmall">{setting.name}</Typography>,
        description: (
          <Typography variant="bodyMedium">{setting.description}</Typography>
        ),
        product: (
          <Typography variant="bodyMedium">{setting.product}</Typography>
        ),
        application: (
          <Typography variant="bodyMedium">{setting.application}</Typography>
        ),
        module: <Typography variant="bodyMedium">{setting.module}</Typography>,
        type: <Typography variant="bodyMedium">{setting.type}</Typography>,
        default: (
          <Typography variant="bodyMedium">
            {typeof setting.default === "object"
              ? JSON.stringify(setting.default)
              : String(setting.default)}
          </Typography>
        ),
        value: (
          <Typography variant="bodyMedium">
            {setting.accountSettings?.value !== undefined &&
            setting.accountSettings?.value !== null
              ? typeof setting.accountSettings.value === "object"
                ? JSON.stringify(setting.accountSettings.value)
                : String(setting.accountSettings.value)
              : "-"}
          </Typography>
        ),
        active: (
          <Chip
            sx={{
              bgcolor: setting.active
                ? theme.palette.brand.light
                : theme.palette.grey[300],
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: setting.active
                ? theme.palette.brand.main
                : theme.palette.grey[500],
            }}
            label={
              <Typography variant="labelSmall">
                {setting.active ? "Active" : "Inactive"}
              </Typography>
            }
            size="medium"
          />
        ),
        actions: <ActionMenu id={setting._id} options={actionMenu} />,
      });

      return row;
    });
  }, [settings, theme]);

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: false,
      label: "Name",
      align: "left",
    },
    {
      id: "description",
      isSortable: true,
      disablePadding: false,
      label: "Description",
      align: "left",
    },
    {
      id: "product",
      isSortable: true,
      disablePadding: false,
      label: "Product",
      align: "left",
    },
    {
      id: "application",
      isSortable: true,
      disablePadding: false,
      label: "Application",
      align: "left",
    },
    {
      id: "module",
      isSortable: true,
      disablePadding: false,
      label: "Module",
      align: "left",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
    },
    {
      id: "default",
      isSortable: true,
      disablePadding: false,
      label: "Default Value",
      align: "left",
    },
    {
      id: "value",
      isSortable: true,
      disablePadding: false,
      label: "Value",
      align: "left",
    },
    {
      id: "active",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
    },
  ];

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SETTINGS,
        option: OPTIONS.VIEW,
        actionCode: ACTION_CODE.WEB_SETTINGS_VIEW,
        action: "When entering",
        detail: "Viewed the list of settings",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  const previousSearchRef = React.useRef<string>("");
  useEffect(() => {
    if (userInfo) {
      if (
        searchDebounce !== previousSearchRef.current &&
        searchDebounce &&
        searchDebounce.trim().length > 0
      ) {
        previousSearchRef.current = searchDebounce;
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.SETTINGS,
          option: OPTIONS.SEARCH,
          actionCode: ACTION_CODE.WEB_SETTINGS_SEARCH,
          action: "When searching",
          detail: "Searched within the list of settings",
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }
    }
  }, [userInfo, searchDebounce, createAuditLog]);

  const handleDelete = async () => {
    if (!showDeleteModal.setting) return;

    const settingName = showDeleteModal.setting.name;

    deleteSettingOne.mutate(showDeleteModal.setting._id, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.SETTINGS,
          option: OPTIONS.DELETE,
          actionCode: ACTION_CODE.WEB_SETTINGS_DELETE,
          action: "When deleting",
          detail: `Delete setting ${settingName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });

        refetchSettings();
        setShowDeleteModal({ open: false, setting: null });
        showSnackBar("Setting deleted successfully", "success");
      },
      onError: (error: any) => {
        setShowDeleteModal({ open: false, setting: null });
        showSnackBar(
          error?.response?.data?.message || "Failed to delete setting",
          "error",
        );
      },
    });
  };

  return (
    <Box>
      {/* Top Bar - Header with title and buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 0,
        }}
      >
        <Typography variant="headlineMedium">Settings</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isLoadingSettings ? (
            <SkeletonHeaderButtons />
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedSetting(null);
                  setModalMode("create");
                  setIsSettingModalOpen(true);
                }}
              >
                ADD
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters - Search */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 50%" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBox
                theme={theme}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    search: value,
                    pageNumber: 0,
                  }));
                }}
                value={filters.search}
                placeHolder="Search..."
              />
            </Box>
            }
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={totalCount}
            isLoading={isLoadingSettings}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
            messageEmptyData="No items to show"
          />
        </CardContent>
      </Card>

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title="Delete Setting"
          description="Are you sure you want to delete this setting?"
          onCancel={() => {
            setShowDeleteModal({ open: false, setting: null });
          }}
          onConfirm={handleDelete}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}

      {/* Setting Modal */}
      <SettingModal
        open={isSettingModalOpen}
        mode={modalMode}
        setting={selectedSetting}
        onClose={() => {
          setIsSettingModalOpen(false);
          setSelectedSetting(null);
        }}
        onSuccess={() => {
          refetchSettings();
        }}
        canAddSetting={true}
        canEditSetting={true}
      />
    </Box>
  );
};

export default SettingsPage;
